/**
 * CONFETTI THAT LOOKS LIKE CONFETTI (2026-10-09, Jack: "enhance and fix it").
 *
 * The old form's confetti is a stock photo of confetti on grey, screen-blended over the hero.
 * Screen keeps every light pixel of that photo, so its out-of-focus background lands on the house
 * as a film of grain — measured on real Bynder heroes, it read as noise, not a party.
 *
 * The editor already supports transparent art: an OVERLAY_FX entry of `{url, mode:"normal"}` is
 * drawn as-is (source-over, cover-fit) instead of screened (vendor/ovSky.js `__fxMode`). So this
 * draws real confetti on a TRANSPARENT layer and swaps it into that same table entry at runtime;
 * the vendored editor is not edited.
 *
 * The art:
 *   · pieces fall from the top and gather at the sides — the middle stays clear for the headline
 *     and the house;
 *   · ribbons, rectangles, squares and dots, each with a darker edge so it reads as paper, and a
 *     soft shadow so white and gold pieces still show against a pale sky;
 *   · festive colours led by gold, white and blue (gold, champagne, white, cobalt, sky, blush).
 *
 * It is SEEDED, so the same confetti comes out on every bake, in every browser — the
 * byte-reproducible rule for any creative choice a requester makes (CLAUDE.md).
 */
import { OVERLAY_FX } from "../vendor/ovSky";

export const CONFETTI_W = 1800;
export const CONFETTI_H = 1260;
const SEED = 0x1e7a2b;

const COLORS = ["#E3B341", "#F2D27A", "#FFFFFF", "#FFFFFF", "#1F6FD1", "#7FC3F2", "#F4A9BC", "#E3B341"];

/** mulberry32 — a tiny seeded PRNG so the layer is identical every time */
export function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Where a piece may land: dense across the top edge and down both sides, thinning toward the
 * centre. `x`,`y` are 0..1. Returns the keep-probability.
 */
export function density(x: number, y: number): number {
  const side = Math.pow(Math.abs(x - 0.5) * 2, 1.6);   /* 0 at centre, 1 at the edges */
  const top = Math.pow(1 - y, 2.2);                     /* 1 at the top, 0 at the bottom */
  const p = Math.min(1, 0.04 + 0.9 * side * (0.45 + 0.55 * (1 - y)) + 0.55 * top * (0.15 + 0.85 * side));
  /* the lockup sits top-centre on every approved hero — keep that zone nearly clear (measured
     2026-10-09: pieces crowding "Coming Soon" made the headline harder to read) */
  const headline = x > 0.22 && x < 0.78 && y < 0.42;
  return headline ? p * 0.2 : p;
}

function shade(hex: string, f: number): string {
  const n = parseInt(hex.slice(1), 16);
  const ch = (s: number) => Math.max(0, Math.min(255, Math.round(((n >> s) & 255) * f)));
  return "rgb(" + ch(16) + "," + ch(8) + "," + ch(0) + ")";
}

/** draw the layer; null where there is no canvas (tests, SSR) */
export function drawConfetti(doc: Document = document): string | null {
  let cv: HTMLCanvasElement;
  try { cv = doc.createElement("canvas"); } catch { return null; }
  cv.width = CONFETTI_W; cv.height = CONFETTI_H;
  const x = cv.getContext && cv.getContext("2d");
  if (!x) return null;
  const r = rng(SEED);
  const W = CONFETTI_W, H = CONFETTI_H;
  let placed = 0, tries = 0;
  while (placed < 230 && tries < 6000) {
    tries++;
    const px = r(), py = r();
    if (r() > density(px, py)) continue;
    placed++;
    const cx = px * W, cy = py * H;
    const col = COLORS[Math.floor(r() * COLORS.length)];
    const kind = r();
    const scale = 0.75 + r() * 0.7 * (1 - py * 0.35);    /* nearer the top reads slightly larger */
    x.save();
    x.translate(cx, cy);
    x.rotate(r() * Math.PI * 2);
    x.shadowColor = "rgba(0,0,0,0.22)";
    x.shadowBlur = 5; x.shadowOffsetX = 1.5; x.shadowOffsetY = 3;
    if (kind < 0.16) {
      /* curled ribbon */
      const len = (70 + r() * 70) * scale, amp = (10 + r() * 12) * scale;
      x.lineCap = "round"; x.lineWidth = (7 + r() * 4) * scale; x.strokeStyle = col;
      x.beginPath(); x.moveTo(-len / 2, 0);
      x.bezierCurveTo(-len / 6, -amp * 2, len / 6, amp * 2, len / 2, 0);
      x.stroke();
      x.shadowColor = "transparent"; x.lineWidth *= 0.35; x.strokeStyle = shade(col, 0.78);
      x.beginPath(); x.moveTo(-len / 2, 2); x.bezierCurveTo(-len / 6, -amp * 2 + 2, len / 6, amp * 2 + 2, len / 2, 2); x.stroke();
    } else if (kind < 0.30) {
      /* dot */
      const rad = (6 + r() * 6) * scale;
      x.fillStyle = col; x.beginPath(); x.arc(0, 0, rad, 0, Math.PI * 2); x.fill();
      x.shadowColor = "transparent"; x.fillStyle = "rgba(255,255,255,0.35)";
      x.beginPath(); x.arc(-rad * 0.3, -rad * 0.3, rad * 0.35, 0, Math.PI * 2); x.fill();
    } else {
      /* paper rectangle / square, foreshortened so some read edge-on as they tumble */
      const sq = kind < 0.45;
      const w = (sq ? 18 + r() * 8 : 26 + r() * 22) * scale;
      const h = (sq ? 18 + r() * 8 : 10 + r() * 7) * scale * (0.35 + r() * 0.65);
      x.fillStyle = col; x.fillRect(-w / 2, -h / 2, w, h);
      x.shadowColor = "transparent";
      x.fillStyle = shade(col, 0.8); x.fillRect(-w / 2, h / 2 - Math.max(1.5, h * 0.22), w, Math.max(1.5, h * 0.22));
    }
    x.restore();
  }
  try { return cv.toDataURL("image/png"); } catch { return null; }
}

let installed = false;
/** swap the editor's confetti entry for the drawn layer (once per page; safe to call often) */
export function installOverlayFx(doc?: Document): boolean {
  if (installed) return true;
  /* jsdom has no canvas (and says so on the console) — the unit tests drive drawConfetti with a stub */
  if (!doc && typeof navigator !== "undefined" && /jsdom/i.test(navigator.userAgent || "")) return false;
  if (!OVERLAY_FX) return false;
  const url = drawConfetti(doc);
  if (!url || url.length < 1000) return false;
  (OVERLAY_FX as Record<string, unknown>).confetti = { url, mode: "normal" };
  installed = true;
  return true;
}
